import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, Mail, MapPin, Send, User, MessageSquare, Building2, CheckCircle2, Clock, Headphones, ExternalLink } from 'lucide-react';
import { contactPage, companyInfo } from '../mock/mock';
import Navbar from './Navbar';
import Footer from './Footer';
import { useToast } from '../hooks/use-toast';

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', organization: '', mobile: '', comments: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.comments) {
      toast({ title: 'Missing details', description: 'Name, email and message are required.' });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      try { localStorage.setItem('dc10g_contact_' + Date.now(), JSON.stringify(form)); } catch (e) {}
      toast({ title: 'Message sent!', description: 'Our team will get back to you within 24 hours.' });
      setForm({ name: '', email: '', organization: '', mobile: '', comments: '' });
      setTimeout(() => setDone(false), 4000);
    }, 900);
  };

  return (
    <>
      <Navbar />

      <section className="relative pt-16 pb-20 section-mesh overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-25" />
        <div className="container-x px-6 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
              <Link to="/" className="hover:text-cyan-300">Home</Link>
              <ChevronRight size={14} />
              <span className="text-cyan-300">Contact Us</span>
            </div>
            <div className="eyebrow mb-4 mx-auto"><Headphones size={12} /> We're here to help</div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] tracking-tight">
              Get in <span className="gradient-text">touch!</span>
            </h1>
            <p className="section-subtitle mx-auto mt-6">{contactPage.intro}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 section-mesh">
        <div className="container-x px-6 grid lg:grid-cols-[1.1fr_1fr] gap-10">
          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="glass-strong rounded-3xl p-8 md:p-10 border border-white/10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-extrabold text-white">Send us a message</h2>
              <p className="mt-2 text-slate-400 text-sm">Fill the form below and our team will reach out within 24 hours.</p>

              {done ? (
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-8 p-6 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 text-center">
                  <div className="inline-flex w-14 h-14 rounded-full bg-emerald-500/20 items-center justify-center mb-3">
                    <CheckCircle2 className="text-emerald-400" size={28} />
                  </div>
                  <div className="text-lg font-bold text-white">Thanks, {form.name || 'there'}!</div>
                  <div className="text-sm text-slate-300 mt-1">Your message is on its way. We’ll respond within 24 hours.</div>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Your Name *</label>
                      <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="text" value={form.name} onChange={update('name')} placeholder="John Doe" className="input-dark pl-10" required />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Your Email *</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" className="input-dark pl-10" required />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Your Organization</label>
                      <div className="relative">
                        <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="text" value={form.organization} onChange={update('organization')} placeholder="Acme Inc." className="input-dark pl-10" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Your Mobile</label>
                      <div className="relative">
                        <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="tel" value={form.mobile} onChange={update('mobile')} placeholder="+91 98765 43210" className="input-dark pl-10" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 mb-1.5 block">Your Comments *</label>
                    <div className="relative">
                      <MessageSquare size={16} className="absolute left-3 top-3 text-slate-500" />
                      <textarea value={form.comments} onChange={update('comments')} rows="5" placeholder="Tell us how we can help..." className="input-dark pl-10 pt-3 resize-none" required />
                    </div>
                  </div>
                  <button type="submit" disabled={submitting} className="btn-primary w-full justify-center py-3 disabled:opacity-70">
                    {submitting ? 'Sending...' : <>Send Message <Send size={16} /></>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* Contact details */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="space-y-4">
            {[
              { Icon: Phone, label: 'Phone', value: companyInfo.salesPhone, sub: 'Talk to our sales team', href: `tel:${companyInfo.salesPhone}` },
              { Icon: Mail, label: 'Email', value: companyInfo.email, sub: 'Reach us anytime', href: `mailto:${companyInfo.email}` },
              { Icon: Clock, label: 'Support Hours', value: '24 / 7 / 365', sub: 'Round-the-clock assistance', href: null }
            ].map((c, i) => (
              <motion.a key={c.label} href={c.href || '#'} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="block p-5 rounded-2xl glass hover:border-cyan-400/40 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-cyan-500/25 group-hover:scale-110 transition-transform">
                    <c.Icon className="text-white" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-slate-400 uppercase tracking-widest">{c.label}</div>
                    <div className="text-lg font-bold text-white mt-0.5">{c.value}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.sub}</div>
                  </div>
                </div>
              </motion.a>
            ))}

            {/* Offices */}
            {[contactPage.corporateOffice, contactPage.registeredOffice].map((o, i) => (
              <motion.div key={o.label} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08 }}
                className="p-5 rounded-2xl glass">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-purple-500/25 shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-slate-400 uppercase tracking-widest">{o.label}</div>
                    <div className="text-sm text-slate-200 mt-1 leading-relaxed">{o.address}</div>
                    <a href={o.mapUrl} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-300 hover:text-cyan-200">
                      View on Google Map <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ContactPage;
